import React, { useState, useEffect, useMemo } from 'react';
import { Select, Table, InputNumber, Input, Spin, Typography } from 'antd'; // Select kept for branch picker
import { useDispatch } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import {
  getBankAccountNamesWithStatsAction,
  updateBankAccountLimitsAction,
} from '../store/interestSchemeActions';
import OutlinedButton from '../../../components/ButtonComponent/OutlinedButton';

interface AgentRef {
  _id: string;
  name: string;
}

interface BankRef {
  _id: string;
  name: string;
}

interface BranchRef {
  _id: string;
  name: string;
  code?: string;
}

// One row per accountName + bank + branch combination
interface BankAccountWithStats {
  _id: string;
  accountId: string;
  customerId: string;
  name: string;
  bank: BankRef | null;
  branch: BranchRef | null;
  agent: AgentRef | null;
  maxAmountLimit: number;
  pledgeLimit: number;
  minimumBalancePaid: number;
  currentAmount: number;
  currentPledges: number;
}

interface EditValues {
  maxAmountLimit?: number;
  pledgeLimit?: number;
  minimumBalancePaid?: number;
}

export default function BankAccountLimits(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();

  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [branchOptions, setBranchOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [data, setData] = useState<BankAccountWithStats[]>([]);
  const [editValues, setEditValues] = useState<Record<string, EditValues>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [bankFilter, setBankFilter] = useState<string | undefined>(undefined);
  const [nameSearch, setNameSearch] = useState<string>('');

  useEffect(() => {
    const stored = localStorage.getItem('branches');
    if (stored != null) {
      const parsed: Array<{ code: string; id: string }> = JSON.parse(stored);
      setBranchOptions(parsed.map((b) => ({ label: b.code, value: b.id })));
    }
  }, []);

  const fetchData = async (branchId: string) => {
    setIsLoading(true);
    const result = await getBankAccountNamesWithStatsAction(dispatch, { branch: branchId });
    if (result != null) {
      setData(result as BankAccountWithStats[]);
      setEditValues({});
      setBankFilter(undefined);
    }
    setIsLoading(false);
  };

  const handleSave = async (record: BankAccountWithStats) => {
    if (record.bank == null || record.branch == null) return;
    setSavingId(record._id);
    const vals = editValues[record._id] ?? {};
    const success = await updateBankAccountLimitsAction(dispatch, {
      id: record.accountId,
      bankBranches: [
        {
          bank: record.bank._id,
          branch: record.branch._id,
          maxAmountLimit: vals.maxAmountLimit ?? record.maxAmountLimit,
          pledgeLimit: vals.pledgeLimit ?? record.pledgeLimit,
          minimumBalancePaid: vals.minimumBalancePaid ?? record.minimumBalancePaid,
        },
      ],
    });
    if (success) {
      await fetchData(selectedBranch);
    }
    setSavingId(null);
  };

  const setEditValue = (id: string, field: keyof EditValues, value: number | null | undefined) => {
    setEditValues((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value ?? 0 },
    }));
  };

  const bankFilterOptions = useMemo(() => {
    const seen = new Map<string, string>();
    data.forEach((row) => {
      if (row.bank != null) seen.set(row.bank._id, row.bank.name);
    });
    return Array.from(seen, ([value, label]) => ({ label, value })).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [data]);

  // an incomplete pattern is expected while typing, so fall back to a literal match
  const nameMatcher = useMemo(() => {
    if (nameSearch.trim() === '') return null;
    try {
      return { regex: new RegExp(nameSearch, 'i'), valid: true };
    } catch {
      return { regex: null, valid: false };
    }
  }, [nameSearch]);

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (bankFilter != null && row.bank?._id !== bankFilter) return false;
      if (nameMatcher == null) return true;
      if (nameMatcher.regex != null) return nameMatcher.regex.test(row.name ?? '');
      return (row.name ?? '').toLowerCase().includes(nameSearch.toLowerCase());
    });
  }, [data, bankFilter, nameMatcher, nameSearch]);

  const columns = [
    {
      title: 'Account Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Customer ID',
      dataIndex: 'customerId',
      key: 'customerId',
      render: (_: any, record: BankAccountWithStats) => record.customerId ?? '-',
    },
    {
      title: 'Bank',
      key: 'bank',
      render: (_: any, record: BankAccountWithStats) => record.bank?.name ?? '-',
    },
    {
      title: 'Branch',
      key: 'branch',
      render: (_: any, record: BankAccountWithStats) =>
        record.branch?.code ?? record.branch?.name ?? '-',
    },
    {
      title: 'Agent',
      key: 'agent',
      render: (_: any, record: BankAccountWithStats) => (
        <span style={{ color: record.agent !== null ? 'inherit' : '#94A3B8' }}>
          {record.agent?.name ?? 'No agent'}
        </span>
      ),
    },
    {
      title: 'Max Amount Limit (₹)',
      key: 'maxAmountLimit',
      render: (_: any, record: BankAccountWithStats) => (
        <InputNumber
          value={editValues[record._id]?.maxAmountLimit ?? record.maxAmountLimit ?? 0}
          min={0}
          style={{ width: 150 }}
          onChange={(value) => { setEditValue(record._id, 'maxAmountLimit', value); }}
          formatter={(value) => `₹ ${value ?? 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => Number(value?.replace(/₹\s?|(,*)/g, '') ?? 0)}
        />
      ),
    },
    {
      title: 'Current Amount (₹)',
      key: 'currentAmount',
      render: (_: any, record: BankAccountWithStats) => (
        <span>₹ {(record.currentAmount ?? 0).toLocaleString('en-IN')}</span>
      ),
    },
    {
      title: 'Remaining Amount (₹)',
      key: 'remainingAmount',
      render: (_: any, record: BankAccountWithStats) => {
        const limit = editValues[record._id]?.maxAmountLimit ?? record.maxAmountLimit ?? 0;
        const remaining = limit - (record.currentAmount ?? 0);
        return (
          <span style={{ color: remaining < 0 ? 'red' : 'inherit', fontWeight: 500 }}>
            ₹ {remaining.toLocaleString('en-IN')}
          </span>
        );
      },
    },
    {
      title: 'Pledge Limit',
      key: 'pledgeLimit',
      render: (_: any, record: BankAccountWithStats) => (
        <InputNumber
          value={editValues[record._id]?.pledgeLimit ?? record.pledgeLimit ?? 0}
          min={0}
          precision={0}
          style={{ width: 120 }}
          onChange={(value) => { setEditValue(record._id, 'pledgeLimit', value); }}
        />
      ),
    },
    {
      title: 'Current Pledges',
      key: 'currentPledges',
      render: (_: any, record: BankAccountWithStats) => <span>{record.currentPledges ?? 0}</span>,
    },
    {
      title: 'Remaining Pledges',
      key: 'remainingPledges',
      render: (_: any, record: BankAccountWithStats) => {
        const limit = editValues[record._id]?.pledgeLimit ?? record.pledgeLimit ?? 0;
        const remaining = limit - (record.currentPledges ?? 0);
        return (
          <span style={{ color: remaining < 0 ? 'red' : 'inherit', fontWeight: 500 }}>
            {remaining}
          </span>
        );
      },
    },
    {
      title: 'Minimum Balance Paid (₹)',
      key: 'minimumBalancePaid',
      render: (_: any, record: BankAccountWithStats) => (
        <InputNumber
          value={editValues[record._id]?.minimumBalancePaid ?? record.minimumBalancePaid ?? 0}
          min={0}
          style={{ width: 150 }}
          onChange={(value) => { setEditValue(record._id, 'minimumBalancePaid', value); }}
          formatter={(value) => `₹ ${value ?? 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => Number(value?.replace(/₹\s?|(,*)/g, '') ?? 0)}
        />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: BankAccountWithStats) => (
        <OutlinedButton
          label={savingId === record._id ? 'Saving...' : 'Save'}
          onClick={() => { void handleSave(record); }}
        />
      ),
    },
  ];

  return (
    <div className="bank__account__page">
      <h1>Edit Bank Account Name</h1>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 24 }}>
        <div className="customers__input__container">
          <h4>Branch</h4>
          <Select
            style={{ width: 311, height: 56 }}
            placeholder="Select branch"
            options={branchOptions}
            onChange={(value: string) => { setSelectedBranch(value); }}
            className="deposit__select"
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <OutlinedButton
            label="Search"
            onClick={() => { if (selectedBranch !== '') void fetchData(selectedBranch); }}
          />
        </div>
      </div>
      {selectedBranch !== '' && (
        <Spin spinning={isLoading}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <div className="customers__input__container">
              <h4>Bank</h4>
              <Select
                style={{ width: 240, height: 56 }}
                placeholder="All banks"
                value={bankFilter}
                options={bankFilterOptions}
                onChange={(value?: string) => { setBankFilter(value); }}
                onClear={() => { setBankFilter(undefined); }}
                allowClear
                className="deposit__select"
              />
            </div>
            <div className="customers__input__container">
              <h4>Account Name</h4>
              <Input
                style={{ width: 311, height: 56 }}
                placeholder="Search by name (regex)"
                value={nameSearch}
                status={nameMatcher?.valid === false ? 'warning' : undefined}
                onChange={(e) => { setNameSearch(e.target.value); }}
                allowClear
              />
              {nameMatcher?.valid === false && (
                <Typography.Text type="warning" style={{ fontSize: 12 }}>
                  Incomplete pattern — matching as plain text
                </Typography.Text>
              )}
            </div>
            <Typography.Text type="secondary" style={{ marginBottom: 18 }}>
              {filteredData.length} of {data.length}
            </Typography.Text>
          </div>
          <Table
            dataSource={filteredData}
            columns={columns}
            rowKey="_id"
            pagination={false}
            bordered
            scroll={{ x: 'max-content' }}
            locale={{
              emptyText:
                data.length > 0
                  ? 'No bank account names match the filters'
                  : 'No bank account names found for this branch',
            }}
          />
        </Spin>
      )}
    </div>
  );
}
